// Top app bar — chrome topbar matching the source-of-truth
// (`portal-standalone.html:1780–1830`).
//
// Left → right:
//   • mobile sidebar/megabar toggle (hamburger)
//   • brand mark "the kitchen os" + "Retailer Portal" tag
//   • flex spacer
//   • + New customer  (→ CRM)
//   • + New quote     (→ Quote Builder)
//   • Kit             (→ toast; chat panel is future work)
//   • Apps icon       (→ toast; launcher is future work)
//   • Search icon     (→ toast; global search is future work)
//   • Settings icon   (→ Settings page)
//   • Fullscreen icon (→ toggleFullscreen)
//   • Theme toggle
//   • Sign out
//
// Kira's button is intentionally absent — deferred to v2.

import { renderIcon, confirmDialog, type IconName } from "@tkos/shared-ui";
import { createThemeToggle } from "./theme";
import { logout, onAuthChange, getCachedUser } from "./auth";
import { navigate, onNavigate, getCurrent } from "./router";
import { createFullscreenButton } from "./fullscreen";
import { openAppLauncher } from "./app-launcher";
import { openNewCustomerModal } from "./new-customer-modal";
import { createRadioMiniPlayer } from "./radio-miniplayer";
import { createLiveIndicator } from "./live-indicator";
import { openSpotlight } from "./spotlight";
import { openKit } from "./kit-panel";
import { getBranding, onBrandingChange, PLATFORM_BRAND_NAME, type RetailerBranding } from "./branding";
import type { AuthUser, ToastKind } from "./types";

export interface HeaderOptions {
  /** Called when the mobile hamburger is tapped. */
  onToggleNav: () => void;
  /** Called after a successful logout so the shell can re-render login. */
  onLoggedOut: () => void;
  /** Shared toast surface for stub buttons. */
  showToast: (msg: string, kind?: ToastKind) => void;
  /**
   * Optional mount callback for the Apple-style notifications bell. The
   * header creates an empty slot in its right-hand actions and hands it
   * to this callback. The callback may return a cleanup fn that the
   * header invokes in `destroy()`.
   */
  mountNotifications?: (slot: HTMLElement) => (() => void) | void;
}

export interface HeaderHandle {
  el: HTMLElement;
  /**
   * Mobile "utilities" tray. The header builds the utility buttons
   * (Kit / Radio / search / theme / fullscreen / settings / apps / sign-out)
   * and exposes them here as a self-contained block. The shell appends this
   * inside the megabar drawer; on desktop it's `display: none`, on mobile
   * (≤ 1023px) it surfaces at the bottom of the drawer below the nav items.
   * Why: the topbar collapses to logo + bell + live-dot + hamburger on
   * mobile per Steve's screenshot review (2026-05-21).
   */
  drawerUtils: HTMLElement;
  destroy: () => void;
}

function quickActionBtn(opts: {
  id?: string;
  title: string;
  className?: string;
  icon: IconName;
  label: string;
  onClick: () => void;
}): HTMLButtonElement {
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = `tkos-topbar__quick ${opts.className ?? ""}`.trim();
  if (opts.id) btn.id = opts.id;
  btn.title = opts.title;
  btn.appendChild(renderIcon(opts.icon, { size: "xs" }));
  const span = document.createElement("span");
  span.textContent = opts.label;
  btn.appendChild(span);
  btn.addEventListener("click", opts.onClick);
  return btn;
}

function iconBtn(opts: {
  title: string;
  className?: string;
  icon: IconName;
  onClick: () => void;
}): HTMLButtonElement {
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = `tkos-topbar__icon-btn ${opts.className ?? ""}`.trim();
  btn.title = opts.title;
  btn.setAttribute("aria-label", opts.title);
  btn.appendChild(renderIcon(opts.icon, { size: "sm" }));
  btn.addEventListener("click", opts.onClick);
  return btn;
}

// Tiny HTML/attr escapers — local so we don't pull in a util module just
// for the brand-mark render. Kept identical in semantics to the helpers
// used elsewhere in the chrome.
function escHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}
function escAttr(s: string): string {
  return escHtml(s).replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

export function createHeader(opts: HeaderOptions): HeaderHandle {
  const header = document.createElement("header");
  header.className = "tkos-topbar";
  header.setAttribute("role", "banner");

  // ── Hamburger (mobile only) ────────────────────────────────────────────
  const hamBtn = document.createElement("button");
  hamBtn.type = "button";
  hamBtn.className = "tkos-topbar__ham";
  hamBtn.setAttribute("aria-label", "Toggle navigation");
  hamBtn.appendChild(renderIcon("menu", { size: "md" }));
  hamBtn.addEventListener("click", opts.onToggleNav);

  // ── Back button (all pages except the dashboard home) ──────────────────
  // Lives in the chrome so every page gets a consistent back affordance on
  // both mobile and desktop without each page rendering its own. Goes back
  // through history; falls back to the dashboard when there's nothing to
  // pop (e.g. a deep-linked first load).
  const backBtn = iconBtn({
    title: "Back",
    className: "tkos-topbar__back",
    icon: "chevron-left",
    onClick: () => {
      if (window.history.length > 1) window.history.back();
      else navigate("dashboard");
    },
  });
  const syncBackVisible = (page: string | null): void => {
    backBtn.style.display = page && page !== "dashboard" ? "" : "none";
  };
  syncBackVisible(getCurrent());
  const offBackNav = onNavigate((name) => syncBackVisible(name));

  // ── Brand mark ─────────────────────────────────────────────────────────
  //
  // White-labelled per retailer (2026-05-19): logo image if uploaded,
  // otherwise business-name text. Falls back to the platform mark when
  // no branding is loaded yet (pre-auth, fetch in flight, or fresh
  // retailer with nothing set) — detected via `usingPlatformDefault`,
  // NOT by comparing the name string. Re-renders on
  // `tkos:branding-changed` via the shell-level branding listener.
  const brand = document.createElement("a");
  brand.className = "tkos-topbar__brand";
  brand.href = "#/";

  function renderBrandMark(b: RetailerBranding): void {
    const name = (b.businessName || PLATFORM_BRAND_NAME).trim();
    if (b.logoUrl) {
      // Logo replaces the text mark, max 32px tall so the chrome layout
      // stays put. `alt` carries the business name for screen readers.
      brand.innerHTML = `
        <img class="tkos-topbar__brand-logo" src="${escAttr(b.logoUrl)}" alt="${escAttr(name)}">
      `;
    } else if (b.usingPlatformDefault) {
      // Platform mark. Keyed off the flag, not the name — a retailer could
      // legitimately be called "Kitchengine" and must still render as a
      // retailer, and renaming the product must not reroute this branch.
      brand.innerHTML = `
        <span class="tkos-topbar__brand-text">${escHtml(PLATFORM_BRAND_NAME)}</span>
      `;
    } else {
      brand.innerHTML = `
        <span class="tkos-topbar__brand-text tkos-topbar__brand-text--retailer">${escHtml(name)}</span>
      `;
    }
  }
  renderBrandMark(getBranding());
  const offBranding = onBrandingChange(renderBrandMark);

  // ── Spacer ─────────────────────────────────────────────────────────────
  const spacer = document.createElement("div");
  spacer.className = "tkos-topbar__spacer";

  // ── Quick actions ──────────────────────────────────────────────────────
  // + New customer opens the shared add-customer modal. The modal POSTs to
  // /api/crm/contacts and the server dispatches the welcome SMS + email
  // best-effort. + New quote navigates to the Quote Builder.
  const newCustomer = quickActionBtn({
    title: "Add a new customer",
    icon: "customer",
    label: "New customer",
    onClick: () => openNewCustomerModal({ showToast: opts.showToast }),
  });

  const newQuote = quickActionBtn({
    title: "Open Quote Builder",
    icon: "file",
    label: "New quote",
    // fresh=1 → always a blank quote. Without it the QB rehydrates the last
    // working copy from localStorage and "New quote" reopens the previous one.
    onClick: () => navigate("quote-builder", { fresh: "1" }),
  });

  // ── Kit AI assistant → opens the right-side Kit chat + briefing panel ──
  const kit = quickActionBtn({
    className: "tkos-topbar__quick--kit",
    title: "Kit — your personal AI assistant",
    icon: "sparkle",
    label: "Kit",
    onClick: () => openKit(),
  });

  // ── Apps launcher → Apple-style grid overlay ───────────────────────────
  const apps = iconBtn({
    title: "Apps",
    icon: "apps",
    onClick: () => openAppLauncher({ showToast: opts.showToast }),
  });

  // ── Search → Apple-Spotlight-style universal search overlay ────────────
  const search = iconBtn({
    title: "Search portal (Cmd/Ctrl+K)",
    icon: "search",
    onClick: () => openSpotlight({ showToast: opts.showToast }),
  });

  // ── Settings shortcut ──────────────────────────────────────────────────
  const settings = iconBtn({
    title: "Settings",
    icon: "gear",
    onClick: () => navigate("settings"),
  });

  // ── Fullscreen ─────────────────────────────────────────────────────────
  const fs = createFullscreenButton();

  // ── Theme toggle (kept from prior shell) ───────────────────────────────
  const themeBtn = createThemeToggle();
  themeBtn.classList.add("tkos-topbar__icon-btn");

  // ── Sign out ───────────────────────────────────────────────────────────
  const signOutBtn = iconBtn({
    title: "Sign out",
    icon: "log-out",
    onClick: () => void requestSignOut(),
  });

  async function requestSignOut(): Promise<void> {
    // Shared dark-theme + amber-accent modal (never window.confirm).
    const ok = await confirmDialog(
      "You'll need to sign back in to access your portal.",
      {
        title: "Sign out?",
        confirmLabel: "Sign out",
        cancelLabel: "Stay signed in",
      },
    );
    if (!ok) return;
    signOutBtn.disabled = true;
    try {
      await logout();
    } finally {
      opts.onLoggedOut();
    }
  }

  // Allow the megabar (Settings → Sign out CTA) to trigger sign-out without
  // coupling to this module — it dispatches `tkos:request-signout` on document.
  const onRequestSignout = (): void => void requestSignOut();
  document.addEventListener("tkos:request-signout", onRequestSignout);

  // ── Notifications slot (Apple-style bell + slide-in + panel) ───────────
  // The shell builds the notifications client once and hands us a mount
  // callback. We create an empty span and the shared-ui component appends
  // its bell button inside. Sits between Apps and Search per the spec.
  const notifSlot = document.createElement("span");
  notifSlot.className = "tkos-topbar__notif-slot";
  let notifCleanup: (() => void) | void;
  if (opts.mountNotifications) {
    notifCleanup = opts.mountNotifications(notifSlot);
  }

  // ── Radio mini-player ──────────────────────────────────────────────────
  // Sits between Kit and Apps. Subscribes to the shell-level radio engine,
  // so playback continues while the user navigates between pages.
  const radio = createRadioMiniPlayer({
    onPickerRequested: () => navigate("dashboard"),
  });

  // ── Live indicator — realtime connection status dot ────────────────────
  // Sits next to the notifications bell so users can see at a glance
  // whether the portal is receiving live updates (green pulse), polling
  // in fallback mode (grey), or trying to reconnect (amber). State is
  // owned by shell/realtime.ts.
  const liveIndicator = createLiveIndicator();

  // ── Reactive: hide the whole topbar-right when logged out ──────────────
  //
  // Right cluster splits into two visibility tiers:
  //   • mobile-+-desktop: notifications bell + live-status dot (always on)
  //   • desktop-only:     everything else (Kit / Radio / Apps / Search /
  //                       Settings / Fullscreen / Theme / Sign-out / quick
  //                       actions). On mobile these are moved into the
  //                       drawer's utilities tray (built below) and the
  //                       topbar shows nothing but ham + brand + bell + dot.
  const right = document.createElement("div");
  right.className = "tkos-topbar__right";

  const rightDesktopOnly = document.createElement("div");
  rightDesktopOnly.className = "tkos-topbar__right-desk";
  rightDesktopOnly.append(newCustomer, newQuote, kit, radio, apps, search, settings, fs.el, themeBtn, signOutBtn);

  // Notifications + live-dot stay in the topbar even on mobile.
  right.append(rightDesktopOnly, notifSlot, liveIndicator.el);

  const renderForUser = (user: AuthUser | null): void => {
    right.style.display = user ? "" : "none";
  };
  renderForUser(getCachedUser());
  const offAuth = onAuthChange(renderForUser);

  // ── Drawer utilities tray ──────────────────────────────────────────────
  //
  // Built here so the buttons are owned by the header module (single source
  // of truth for click handlers, theme/fullscreen state); shell.ts appends
  // `drawerUtils` into the megabar element so it lives inside the slide-in
  // drawer on mobile. We re-create button instances rather than moving the
  // topbar buttons because (a) DOM nodes can only live in one place, (b)
  // the topbar styling on .tkos-topbar__quick uses small font + condensed
  // padding that doesn't suit a 48-px-tall drawer row.
  const drawerUtils = document.createElement("div");
  drawerUtils.className = "tkos-drawer-utils";

  function drawerBtn(
    label: string,
    icon: IconName,
    onClick: () => void,
    perm?: string,
  ): HTMLButtonElement {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "tkos-drawer-utils__btn";
    if (perm) btn.setAttribute("data-perm", perm);
    btn.appendChild(renderIcon(icon, { size: "sm" }));
    const span = document.createElement("span");
    span.textContent = label;
    btn.appendChild(span);
    btn.addEventListener("click", onClick);
    return btn;
  }

  // The drawer auto-closes after navigation via shell.ts's onAfterNavigate
  // hook on the megabar; non-navigating items (theme/fullscreen) leave the
  // drawer open so the user can compare states.
  function closeDrawer(): void {
    document.dispatchEvent(new CustomEvent("tkos:close-mobile-nav"));
  }

  // Separator label.
  const utilHeading = document.createElement("div");
  utilHeading.className = "tkos-drawer-utils__heading";
  utilHeading.textContent = "Utilities";
  drawerUtils.appendChild(utilHeading);

  const utilGrid = document.createElement("div");
  utilGrid.className = "tkos-drawer-utils__grid";
  drawerUtils.appendChild(utilGrid);

  utilGrid.append(
    drawerBtn("New customer", "customer", () => {
      closeDrawer();
      openNewCustomerModal({ showToast: opts.showToast });
    }, "crm"),
    drawerBtn("New quote", "file", () => {
      closeDrawer();
      navigate("quote-builder", { fresh: "1" });
    }, "crm"),
    drawerBtn("Kit", "sparkle", () => {
      closeDrawer();
      openKit();
    }, "ai-tools"),
    drawerBtn("Radio", "music", () => {
      closeDrawer();
      navigate("dashboard");
    }, "radio"),
    drawerBtn("Search", "search", () => {
      closeDrawer();
      openSpotlight({ showToast: opts.showToast });
    }),
    drawerBtn("Apps", "apps", () => {
      closeDrawer();
      openAppLauncher({ showToast: opts.showToast });
    }),
    drawerBtn("Settings", "gear", () => {
      closeDrawer();
      navigate("settings");
    }, "settings"),
    drawerBtn("Theme", "sun", () => {
      themeBtn.click();
    }),
    drawerBtn("Fullscreen", "fullscreen", () => {
      closeDrawer();
      fs.el.click();
    }),
    drawerBtn("Sign out", "log-out", () => {
      closeDrawer();
      void requestSignOut();
    }),
  );

  // ── Assemble ───────────────────────────────────────────────────────────
  const brandWrap = document.createElement("div");
  brandWrap.className = "tkos-topbar__brand-wrap";
  brandWrap.append(brand);

  header.append(hamBtn, backBtn, brandWrap, spacer, right);

  return {
    el: header,
    drawerUtils,
    destroy: () => {
      offAuth();
      offBranding();
      offBackNav();
      fs.destroy();
      liveIndicator.destroy();
      document.removeEventListener("tkos:request-signout", onRequestSignout);
      if (typeof notifCleanup === "function") notifCleanup();
    },
  };
}
